import { NavLink, Outlet } from 'react-router';
import type { Permission } from '@endcopilot/shared';
import { useSession } from '../lib/session';
import { Badge, cx } from '../components/ui';
import { Brand } from '../pages/LoginPage';
import { ROLE_LABEL } from '../lib/labels';

interface NavItem {
  to: string;
  label: string;
  permission?: Permission;
  superadmin?: boolean;
  needsTenant?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Início' },
  { to: '/empresas', label: 'Empresas', superadmin: true },
  { to: '/usuarios', label: 'Usuários', permission: 'users:read', needsTenant: true },
  { to: '/grupos', label: 'Grupos', permission: 'groups:read', needsTenant: true },
  { to: '/auditoria', label: 'Auditoria', permission: 'audit:read', needsTenant: true },
  { to: '/conta', label: 'Minha conta' },
];

function TenantPicker() {
  const { tenants, tenant, selectTenant, me } = useSession();
  if (tenants.length === 0) {
    return (
      <span className="text-sm text-slate-500">
        {me?.user.isSuperadmin ? 'Nenhuma empresa cadastrada' : 'Sem empresa vinculada'}
      </span>
    );
  }
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-slate-500">Empresa</span>
      <select
        aria-label="Empresa selecionada"
        value={tenant?.id ?? ''}
        onChange={(e) => selectTenant(e.target.value)}
        className="max-w-56 rounded-md border-0 py-1.5 pr-8 pl-2 text-sm ring-1 ring-slate-300 focus:ring-2 focus:ring-indigo-600"
      >
        {!tenant && <option value="">Selecione…</option>}
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
            {t.status === 'SUSPENDED' ? ' (suspensa)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

export function AppShell() {
  const session = useSession();
  const { me, role, tenant, can } = session;
  const visible = NAV.filter((n) => {
    if (n.superadmin) return me?.user.isSuperadmin;
    if (n.permission) return tenant !== null && can(n.permission);
    return true;
  });

  const link = ({ isActive }: { isActive: boolean }) =>
    cx(
      'block whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium',
      isActive ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-100',
    );

  return (
    <div className="min-h-dvh bg-slate-50 md:grid md:grid-cols-[14rem_1fr]">
      <aside className="border-b border-slate-200 bg-white md:min-h-dvh md:border-r md:border-b-0">
        <div className="flex items-center justify-between px-4 py-4">
          <Brand />
        </div>
        <nav
          aria-label="Principal"
          className="flex gap-1 overflow-x-auto px-3 pb-3 md:flex-col md:overflow-visible"
        >
          {visible.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} className={link}>
              {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="min-w-0">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 md:px-8">
          <TenantPicker />
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-slate-700 sm:inline">{me?.user.name}</span>
            {role && (
              <Badge tone={role === 'SUPERADMIN' ? 'indigo' : 'slate'}>{ROLE_LABEL[role]}</Badge>
            )}
            <button
              type="button"
              onClick={() => void session.logout()}
              className="rounded-md px-2 py-1 text-slate-600 hover:bg-slate-100"
            >
              Sair
            </button>
          </div>
        </header>
        <main className="px-4 py-6 md:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
